import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, MessageCircle, ClipboardCheck, Rocket, ShieldCheck, Wallet, Undo2, Film, Languages, MapPin, Check,
  Phone, ThumbsUp, MessageSquare, Share2, Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';

const TAGS = [
  'Bakeries', 'Salons', 'Clinics', 'Coaching classes', 'Restaurants', 'Web & app development', 'Agencies & consultants',
  'Real estate', 'Startups', 'Enterprises', 'Gyms', 'Shops', 'Events', 'Any business',
];
const TAG_COLOURS = ['tag-sky', 'tag-mint', 'tag-blush', 'tag-butter'];

const STEPS = [
  { icon: MessageCircle, bg: 'bg-[#7CC6FE]', title: 'Chat about your business', desc: 'Say what you sell and who it is for, in English or Hinglish. We ask at most three short questions.' },
  { icon: ClipboardCheck, bg: 'bg-[#FFE49A]', title: 'Check your ad plan', desc: 'We pick the best kind of ad, the right area and the audience. Change anything with one tap.' },
  { icon: Rocket, bg: 'bg-[#FFB8D1]', title: 'Launch with your budget', desc: 'We write the ad, make the photo and a video, check it against Meta’s rules and run it.' },
];

const FEATURES = [
  { icon: Film, title: 'Photo and video', desc: 'A picture and a short video with a voiceover for every ad.', tag: 'tag-blush' },
  { icon: MapPin, title: 'The right area', desc: 'Your neighbourhood, your city or all of India.', tag: 'tag-sky' },
  { icon: ShieldCheck, title: 'Checked first', desc: 'Checked against Meta’s rules before you pay.', tag: 'tag-mint' },
  { icon: Languages, title: 'English or Hindi', desc: 'In the language your customers speak.', tag: 'tag-butter' },
];

// Illustrations of the kinds of ads Rayogent makes (made-up businesses, labelled as examples on the page).
const EXAMPLES = [
  { name: 'Sharma Bakery', place: 'Pune', goal: 'Get calls', text: 'Make their day sweeter. Fresh birthday cakes, baked to order.',
    art: { bg: '#FF7A1A', fg: '#141414', kicker: 'This week only', big: '20% OFF', small: 'Birthday cakes' }, cta: 'Call now', ctaIcon: Phone },
  { name: 'Glow Studio', place: 'Indore', goal: 'Get WhatsApp chats', text: 'Look your best on the big day. Book a bridal trial today.',
    art: { bg: '#FFB8D1', fg: '#141414', kicker: 'Bridal makeup', big: '₹499', small: 'Trial session' }, cta: 'Send message', ctaIcon: MessageSquare },
  { name: 'PixelCraft', place: 'Bengaluru', goal: 'Get leads', text: 'Your business deserves a website that brings customers.',
    art: { bg: '#141414', fg: '#FFF4DD', kicker: 'Websites & apps', big: '7 days', small: 'From idea to live', accent: '#FF7A1A' }, cta: 'Get quote', ctaIcon: Globe },
  { name: 'Bright Minds', place: 'Jaipur', goal: 'Get sign-ups', text: 'Small batches, big results. Seats are filling fast.',
    art: { bg: '#7CC6FE', fg: '#141414', kicker: 'New JEE batch', big: '1 June', small: 'Free demo class' }, cta: 'Sign up', ctaIcon: ArrowRight },
];

const MONEY = [
  { icon: Wallet, text: 'Your first ad is free. Then 3 ads for ₹200 on a 7-day trial, or 5 ads for ₹2,500 a month. Ad spend is only what Meta spends, plus a 15% fee and GST.' },
  { icon: Undo2, text: 'Whatever your ad doesn’t spend comes back to your wallet.' },
  { icon: ShieldCheck, text: 'Payments by Razorpay: UPI, cards and net banking.' },
];

/** The picture part of an ad: a bold poster drawn in CSS. */
function AdArt({ art, compact = false }) {
  return (
    <div className="relative aspect-square overflow-hidden flex flex-col justify-center px-5" style={{ background: art.bg, color: art.fg }}>
      <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full border-[3px]" style={{ borderColor: art.fg, opacity: 0.25 }} />
      <span className="absolute -left-6 -bottom-10 w-24 h-24 rounded-full" style={{ background: art.accent || art.fg, opacity: art.accent ? 1 : 0.12 }} />
      <p className={`relative font-semibold uppercase tracking-wider ${compact ? 'text-[10px]' : 'text-xs'}`}>{art.kicker}</p>
      <p className={`relative font-heading font-extrabold leading-none my-1 ${compact ? 'text-4xl' : 'text-5xl'}`}>{art.big}</p>
      <p className={`relative font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>{art.small}</p>
    </div>
  );
}

/** A Facebook-style feed ad. */
function AdCard({ ad, compact = false }) {
  const CtaIcon = ad.ctaIcon;
  return (
    <div className="bg-white text-[#141414] rounded-xl overflow-hidden border-2 border-[#141414]">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="w-8 h-8 rounded-full border-2 border-[#141414] flex items-center justify-center font-heading font-extrabold text-xs"
          style={{ background: ad.art.bg === '#141414' ? '#FF7A1A' : ad.art.bg }}>{ad.name[0]}</span>
        <div className="leading-tight min-w-0">
          <p className="text-xs font-bold truncate">{ad.name}</p>
          <p className="text-[10px] text-[#6b6b6b]">Sponsored · {ad.place}</p>
        </div>
      </div>
      <p className={`px-3 pb-2 ${compact ? 'text-[11px]' : 'text-xs'} leading-snug`}>{ad.text}</p>
      <AdArt art={ad.art} compact={compact} />
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#F4F1EA] border-t-2 border-[#141414]">
        <p className="text-[11px] font-semibold truncate">{ad.art.small}</p>
        <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#141414] text-white text-[11px] font-semibold">
          <CtaIcon className="w-3 h-3" aria-hidden="true" /> {ad.cta}
        </span>
      </div>
      {!compact && (
        <div className="flex justify-around py-1.5 text-[11px] text-[#6b6b6b] border-t border-[#e5e0d5]">
          <span className="inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" aria-hidden="true" /> Like</span>
          <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" aria-hidden="true" /> Comment</span>
          <span className="inline-flex items-center gap-1"><Share2 className="w-3 h-3" aria-hidden="true" /> Share</span>
        </div>
      )}
    </div>
  );
}

/** The hero phone: a Hinglish message turns into a finished ad, on a loop. */
function HeroPhone() {
  const reduce = useReducedMotion();
  const [stage, setStage] = useState(reduce ? 2 : 0);
  useEffect(() => {
    if (reduce) return undefined;
    const [nextStage, wait] = { 0: [1, 1600], 1: [2, 1500], 2: [0, 5200] }[stage];
    const t = setTimeout(() => setStage(nextStage), wait);
    return () => clearTimeout(t);
  }, [stage, reduce]);

  return (
    <div className="relative w-[290px] shrink-0" aria-hidden="true">
      <div className="h-[580px] rounded-[42px] bg-[#141414] p-[10px] shadow-[10px_10px_0_0_hsl(var(--brand))]">
        <div className="w-full h-full rounded-[33px] bg-[#FFF8EC] p-3.5 flex flex-col gap-3 overflow-hidden">
          <div className="flex items-center justify-between pt-1 pb-2 border-b-2 border-[#141414]/10 text-[#141414]">
            <span className="flex items-center gap-1.5"><Logo size="sm" withWord={false} /><span className="font-heading font-extrabold">Rayogent</span></span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#B8F2C9] border-2 border-[#141414]">Online</span>
          </div>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} key={stage === 0 ? 'msg-new' : 'msg'}
            className="self-end max-w-[85%] px-3 py-2 rounded-2xl rounded-br-sm bg-[#FF7A1A] border-2 border-[#141414] text-[#141414] text-[13px] font-medium leading-snug">
            Meri bakery hai Pune mein. Birthday cakes pe 20% off chahiye, calls aaye.
          </motion.div>
          <AnimatePresence mode="wait">
            {stage === 1 && (
              <motion.div key="typing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="self-start flex gap-1 px-3 py-3 rounded-2xl rounded-bl-sm bg-white border-2 border-[#141414] text-[#141414]">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </motion.div>
            )}
            {stage === 2 && (
              <motion.div key="ad" initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 220, damping: 20 }} className="flex flex-col gap-2">
                <p className="self-start text-[11px] font-semibold px-2 py-1 rounded-lg bg-white border-2 border-[#141414] text-[#141414]">
                  Your ad is ready ✓
                </p>
                <AdCard ad={EXAMPLES[0]} compact />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <motion.span initial={{ rotate: 6 }} animate={reduce ? undefined : { rotate: [6, 2, 6] }} transition={{ duration: 4, repeat: Infinity }}
        className="absolute -right-5 top-24 px-3 py-1.5 rounded-xl bg-[#B8F2C9] border-[2.5px] border-[#141414] shadow-neo-sm text-[#141414] text-xs font-bold">
        Live on FB + Insta
      </motion.span>
      <span className="absolute -left-6 bottom-28 -rotate-6 px-3 py-1.5 rounded-xl bg-[#FFE49A] border-[2.5px] border-[#141414] shadow-neo-sm text-[#141414] text-xs font-bold">
        ₹150/day · 7 days
      </span>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background dot-grid overflow-x-hidden">
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b-2 border-ink">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Rayogent home" data-testid="header-logo"><Logo /></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login"><Button variant="ghost" size="sm" data-testid="login-link">Log in</Button></Link>
            <Link to="/register">
              <Button size="sm" className="gradient-bg" data-testid="signup-link">Get started <ArrowRight className="w-3.5 h-3.5" /></Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-14">
          <motion.div className="flex-1 min-w-0" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <p className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-card border-2 border-ink shadow-neo-sm text-sm font-semibold">
              <span className="w-2 h-2 rounded-full bg-brand border border-ink" aria-hidden="true" /> Marketing in 3 taps
            </p>
            <h1 className="text-[44px] leading-[1.02] sm:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 font-heading">
              Let Rayogent be your{' '}
              <span className="inline-block px-3 pb-1 bg-brand text-[#141414] border-[3px] border-ink rounded-2xl shadow-neo -rotate-2">Marketer</span>.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed max-w-xl mb-8">
              Tell us what you sell, in English or Hinglish. Rayogent plans your ad, writes it, designs it, makes the
              video and runs it on Facebook &amp; Instagram. You only pay for what Meta spends.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Button size="lg" className="gradient-bg !font-extrabold text-lg h-14 px-8" onClick={() => navigate('/register')} data-testid="get-started-btn">
                Make my ad <ArrowRight className="w-5 h-5" />
              </Button>
              <span className="text-sm font-medium">From ₹1,000. Unspent money comes back.</span>
            </div>
          </motion.div>
          <HeroPhone />
        </div>
      </section>

      {/* Business types ticker */}
      <div className="bg-ink border-y-2 border-ink py-3 overflow-hidden">
        <p className="sr-only">Rayogent works for every kind of business: {TAGS.join(', ')}.</p>
        <div className="marquee gap-3 pr-3" aria-hidden="true">
          {[...TAGS, ...TAGS].map((t, i) => (
            <span key={i} className={`tag ${TAG_COLOURS[i % 4]} shrink-0`}>{t}</span>
          ))}
        </div>
      </div>

      {/* How it works */}
      <section className="px-4 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-3 font-heading">From one message to a live ad</h2>
          <p className="text-lg text-muted-foreground mb-12 max-w-2xl">No Ads Manager, no designer, no agency. Three taps.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} className={`${s.bg} text-[#141414] rounded-2xl border-[2.5px] border-[#141414] shadow-neo p-6 relative`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <span className="absolute -top-5 left-6 w-11 h-11 rounded-xl bg-[#141414] text-[#FFF4DD] flex items-center justify-center font-heading font-extrabold text-lg">{i + 1}</span>
                <s.icon className="w-7 h-7 mt-4 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-extrabold mb-2 font-heading">{s.title}</h3>
                <p className="text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example ads */}
      <section className="px-4 py-16 md:py-24 bg-card border-y-2 border-ink">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-3 font-heading">Every kind of business, every kind of ad</h2>
              <p className="text-lg text-muted-foreground max-w-2xl">Shops, services, startups and enterprises. Get calls, WhatsApp chats, website visits, leads, or more likes and shares.</p>
            </div>
            <span className="text-xs font-semibold text-muted-foreground shrink-0">Example ads, for illustration</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXAMPLES.map((ad, i) => (
              <motion.div key={ad.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06 }} className={i % 2 ? 'lg:mt-8' : ''}>
                <div className="shadow-neo rounded-xl hover:-translate-y-1 transition-transform"><AdCard ad={ad} /></div>
                <span className={`tag ${TAG_COLOURS[i]} mt-3`}>{ad.goal}</span>
              </motion.div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {FEATURES.map(f => (
              <div key={f.title} className="flex gap-3 items-start">
                <span className={`tag ${f.tag} !rounded-xl w-11 h-11 justify-center shrink-0`}><f.icon className="w-5 h-5" aria-hidden="true" /></span>
                <div>
                  <h3 className="font-bold font-heading">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-snug">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Money */}
      <section className="px-4 py-16 md:py-24 bg-[#141414] text-[#FFF4DD]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight font-heading mb-4">Clear money, <span className="text-[#FF7A1A]">no surprises</span></h2>
            <p className="text-lg mb-8 text-[#FFF4DD]/80">Before you launch you see exactly what goes to Meta, our fee and GST.</p>
            <ul className="space-y-4">
              {MONEY.map(m => (
                <li key={m.text} className="flex gap-3 items-start">
                  <span className="w-9 h-9 rounded-lg bg-[#FF7A1A] border-2 border-[#FFF4DD] flex items-center justify-center shrink-0 text-[#141414]"><m.icon className="w-4 h-4" aria-hidden="true" /></span>
                  <span className="text-base pt-1.5">{m.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#FFF4DD] text-[#141414] rounded-2xl border-[2.5px] border-[#FFF4DD] shadow-[8px_8px_0_0_#FF7A1A] p-6 sm:p-8 max-w-md lg:ml-auto w-full">
            <p className="text-xs font-bold uppercase tracking-wider mb-4">Example: 7 days at ₹150 a day</p>
            <dl className="space-y-3 text-base">
              <div className="flex justify-between gap-3"><dt>₹150 × 7 days (goes to Meta)</dt><dd className="font-semibold">₹1,050</dd></div>
              <div className="flex justify-between gap-3"><dt>Our fee (15%)</dt><dd>₹157.50</dd></div>
              <div className="flex justify-between gap-3"><dt>GST (18% of the fee)</dt><dd>₹28.35</dd></div>
              <div className="flex justify-between items-baseline gap-3 font-bold border-t-2 border-dashed border-[#141414] pt-3">
                <dt>Held from your wallet</dt><dd className="font-heading text-3xl font-extrabold">₹1,235.85</dd>
              </div>
            </dl>
            <p className="text-sm mt-4 font-medium">If Meta spends less, the rest comes back.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 py-16 md:py-24 bg-brand border-b-2 border-ink text-[#141414]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-6xl font-extrabold tracking-tight mb-4 font-heading">Your next customer is scrolling right now.</h2>
          <p className="text-lg mb-8 font-medium">Your first ad takes a few minutes.</p>
          <Button size="lg" className="!font-extrabold text-lg h-14 px-10 bg-[#141414] text-[#FFF4DD] border-[2.5px] border-[#141414] shadow-[5px_5px_0_0_#FFF4DD] hover:bg-[#141414] hover:-translate-y-0.5 transition-transform"
            onClick={() => navigate('/register')} data-testid="cta-get-started-btn">
            Make my ad <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="mt-6 text-sm font-semibold flex items-center justify-center gap-2"><Check className="w-4 h-4" aria-hidden="true" /> No Meta Ads Manager needed</p>
        </div>
      </section>

      <footer className="py-8 px-4 bg-card">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <p className="text-sm text-muted-foreground">Marketing made easy for every business in India.</p>
        </div>
      </footer>
    </div>
  );
}
